import api from "../../../utils/api";
import type { FirestoreTimestamp } from "./leadsService";

// ============================================================================
// CRM Contacts (Contactos) — frontend service. A contact is a person who is no
// longer an active lead. Leads are never hard deleted in the Bandeja — they are
// CONVERTED into a contact (POST /crm/contacts/from-lead), which preserves the
// person here and removes the lead from the inbox / pipeline / call lists.
//   • list    (authed): GET    /crm/contactsList   (optional ?companyId=)
//   • get     (authed): GET    /crm/contacts?id=
//   • update  (authed): PUT    /crm/contacts?id=
//   • delete  (authed): DELETE /crm/contacts?id=
//   • convert (authed): POST   /crm/contacts/from-lead?leadId=
// ============================================================================

export type ContactStage = "lead" | "customer" | "inactive";

export interface Contact {
  id: string;
  name: string | null;
  company: string | null;
  companyId?: string | null;
  email: string | null;
  whatsapp: string | null;
  /** Plain phone (E.164) when the number is NOT a WhatsApp line — carried over
   *  from the lead on conversion, same as `Lead.phone`. */
  phone?: string | null;
  role: string | null;
  product: string | null;
  source: string | null;
  stage: ContactStage;
  notes: string | null;
  /** Qualification enrichment copied from the lead (#220). */
  interestService?: string | null;
  acquisitionSource?: string | null;
  referralName?: string | null;
  tags?: string[];
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

export interface ContactsPage {
  data: Contact[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

interface ContactsListParams {
  page?: number;
  limit?: number;
  companyId?: string;
}

export async function fetchContacts(params: ContactsListParams = {}): Promise<ContactsPage> {
  const { page = 1, limit = 200, companyId } = params;
  const res = await api.get("/crm/contactsList", {
    params: { page, limit, ...(companyId ? { companyId } : {}) },
  });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

export async function updateContact(
  id: string,
  patch: Partial<Pick<Contact, "name" | "email" | "whatsapp" | "phone" | "role" | "company" | "stage" | "notes">>,
): Promise<Contact> {
  const res = await api.put(`/crm/contacts`, patch, { params: { id } });
  return res.data?.data;
}

export async function deleteContact(id: string): Promise<void> {
  await api.delete(`/crm/contacts`, { params: { id } });
}

/** Convert a lead into a contact (the Bandeja "remove" action). */
export async function convertLeadToContact(leadId: string): Promise<Contact> {
  const res = await api.post(`/crm/contacts/from-lead`, {}, { params: { leadId } });
  return res.data?.data;
}
